import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Seo from '../components/seo/Seo.jsx';
import ContactCard from '../components/contact/ContactCard.jsx';
import FoxFigure from '../components/illustrations/FoxFigure.jsx';
import BusyIllustration from '../components/illustrations/BusyIllustration.jsx';
import { EasyIcon, PlainWordsIcon, PlugIcon } from '../components/illustrations/BeliefIcons.jsx';
import { CONTACTS } from '../content/contacts.js';
import './AboutPage.css';

// One small drawing per belief, in the same order as `about.beliefs`
const BELIEF_ICONS = [EasyIcon, PlugIcon, PlainWordsIcon];

/**
 * "Om oss": who we are and why we started Revetall (the last paragraph is the point and stands
 * out, next to a drawing of everything else burning), what we believe in, and the two people
 * to contact (from CONTACTS). That last section is `#kontakt`: every "Prøv gratis" button on
 * the site leads to it. Texts come from `about`.
 * @returns {JSX.Element} About page
 */
function AboutPage() {
  const { t } = useTranslation();
  const why = t('about.why', { returnObjects: true });
  const beliefs = t('about.beliefs', { returnObjects: true });

  return (
    <>
      <Seo title={t('about.seoTitle')} description={t('about.seoDescription')} />

      <section className="about-hero">
        <div className="about-hero-content">
          <div>
            <Link to="/" className="about-back-link">
              <span aria-hidden="true">←</span> {t('featurePage.back')}
            </Link>
            <h1>{t('about.title')}</h1>
            <p className="about-hero-lead">{t('about.lead')}</p>
          </div>
          <div className="about-hero-art">
            <FoxFigure />
          </div>
        </div>
      </section>

      <section className="about-section about-tinted">
        <div className="about-why">
          <div>
            <h2>{t('about.whyTitle')}</h2>
            {why.map((paragraph, index) => (
              <p
                key={paragraph}
                className={`about-why-text${index === why.length - 1 ? ' is-point' : ''}`}
              >
                {paragraph}
              </p>
            ))}
          </div>
          <div className="about-why-art">
            <BusyIllustration />
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="about-wide">
          <h2>{t('about.beliefsTitle')}</h2>
          <ul className="about-beliefs" role="list">
            {beliefs.map((belief, index) => {
              const Icon = BELIEF_ICONS[index];

              return (
                <li key={belief.title} className="about-belief">
                  {Icon && (
                    <span className="about-belief-icon">
                      <Icon />
                    </span>
                  )}
                  <h3>{belief.title}</h3>
                  <p>{belief.text}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="about-section about-tinted" id="kontakt" aria-labelledby="kontakt-title">
        <div className="about-contacts">
          <h2 id="kontakt-title">{t('about.contactTitle')}</h2>
          <p className="about-contacts-lead">{t('about.contactLead')}</p>
          <div className="about-contacts-grid">
            {CONTACTS.map((contact) => (
              <ContactCard key={contact.id} contact={contact} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default AboutPage;
